// User Profile and Booking Service
// Integrates with backend API for user data and bookings
// =====================================================

import {
  resolveBookingAmounts
} from '../utils/bookingAmounts';
import { resolveBookingActivity } from '../utils/bookingDisplay';
import { bookingHeadcount } from '../utils/bookingHeadcount';
import { isFreeBooking } from '../utils/bookingPayment';
import { bookingContactToThreadMeta } from '../utils/messageOwnership';
import { toCustomerError } from '../utils/customerError';

import { API_BASE_URL } from '../config/apiEndpoints';

class UserProfileService {
  constructor() {
    this.token = null;
    this.user = null;
  }

  // Set authentication token (from Firebase or Google OAuth)
  setAuthToken(token) {
    this.token = token;
  }

  // Set current user data
  setUser(user) {
    this.user = user;
  }

  // Get authentication headers
  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  // Make authenticated API call
  async authenticatedFetch(url, options = {}) {
    const headers = this.getAuthHeaders();
    

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });


      if (!response.ok) {
      let errorData = {};
      let errorText = '';
      try {
        errorText = await response.text();
        try {
          errorData = JSON.parse(errorText);
        } catch (parseError) {
          // If JSON parsing fails, use the text as error message
          errorData = { message: errorText || `HTTP error! status: ${response.status}`, detail: errorText };
        }
      } catch (e) {
        // If parsing fails, use the text as error message
        errorData = { message: errorText || `HTTP error! status: ${response.status}` };
      }


      
      // Provide more detailed error message
      const errorMessage = errorData.detail || errorData.message || errorData.error || '';
      throw new Error(toCustomerError(errorMessage, { status: response.status }));
    }

    return response;
  }

  // Get current user profile from backend
  async getUserProfile() {
    try {

      if (!this.token) {
        throw new Error('No authentication token available');
      }

      // Get user ID from the stored user data or Firebase user
      let userId = null;
      
      // First try to get from stored user data (backend user ID)
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          userId = userData.id; // This is the backend user ID from JWT
        } catch (e) {
        }
      }
      
      // If no stored user ID, try service user (Firebase UID)
      if (!userId && this.user) {
        userId = this.user.uid; // Firebase UID
      }


      // Use GET /users/{user_id} when we have the ID (from localStorage or this.user).
      // Do NOT fall back to /auth/me - it expects backend JWT; frontend uses Firebase token and gets 401.
      if (!userId) {
        throw new Error('No user ID available for profile fetch');
      }

      const response = await this.authenticatedFetch(`${API_BASE_URL}/users/${userId}`);

      if (!response.ok) {
        // 404 = user not in DB yet; let fallback handle it
        throw new Error(`Profile fetch failed: ${response.status}`);
      }

      const userProfile = await response.json();


      const transformedProfile = {
        id: userProfile.id,
        firstName: userProfile.first_name || userProfile.firstName || '',
        lastName: userProfile.last_name || userProfile.lastName || '',
        email: userProfile.email || '',
        phone: userProfile.phone || '',
        company: userProfile.company || '',
        bio: userProfile.bio || '',
        organisation: userProfile.organisation || '',
        jobTitle: userProfile.job_title || userProfile.job_title || 'customer',
        profileImageUrl: userProfile.profile_image_url || userProfile.avatar_url || '',
        createdAt: userProfile.created_at || userProfile.createdAt || '',
        updatedAt: userProfile.updated_at || userProfile.updatedAt || ''
      };

      return transformedProfile;
    } catch (error) {

      // Fallback to Firebase user data if backend fails
      if (this.user) {

        const fallbackProfile = {
          id: this.user.uid,
          firstName: this.user.displayName?.split(' ')[0] || this.user.firstName || '',
          lastName: this.user.displayName?.split(' ').slice(1).join(' ') || this.user.lastName || '',
          email: this.user.email || '',
          phone: this.user.phone || '',
          company: this.user.company || '',
          bio: this.user.bio || '',
          role: this.user.role || this.user.user_type || 'customer',
          profileImageUrl: this.user.photoURL || '',
          createdAt: '',
          updatedAt: ''
        };
        return fallbackProfile;
      }
      
      throw error;
    }
  }

  // Update user profile
  async updateUserProfile(profileData, userIdFromCaller = null) {
    try {
      if (!this.token) {
        throw new Error('No authentication token available');
      }

      // Map frontend field names to backend field names
      const phoneTrimmed = String(profileData.phone ?? '').trim();
      const updateData = {
        first_name: profileData.firstName || '',
        last_name: profileData.lastName || '',
        company: profileData.organisation || profileData.company || '',
        bio: profileData.bio || '',
        profile_image_url: profileData.profileImageUrl || ''
      };
      // Empty string violates DB check constraint users_phone_check; use null or omit.
      if (phoneTrimmed) {
        updateData.phone = phoneTrimmed;
      } else {
        updateData.phone = null;
      }

      // Add job_title if it exists (some backends might expect this)
      if (profileData.jobTitle) {
        updateData.job_title = profileData.jobTitle;
      }

      // Try to get user ID from multiple sources for logging
      let userIdFromStorage = null;
      let userIdFromService = null;
      let userIdFromToken = null;

      // Get from stored user data
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          userIdFromStorage = userData.id;
        } catch (e) {
        }
      }
      
      // Get from service user
      if (this.user) {
        userIdFromService = this.user.uid || this.user.id;
      }

      // Try to decode JWT token to get user_id
      if (this.token) {
        try {
          const tokenPayload = JSON.parse(atob(this.token.split('.')[1]));
          userIdFromToken = tokenPayload.user_id || tokenPayload.sub || tokenPayload.id;
        } catch (e) {
        }
      }

      // Determine which ID to use (for logging purposes)
      const userId = userIdFromToken || userIdFromStorage || userIdFromService;


      // Use the userIdFromCaller parameter if provided (more reliable - comes from AccountDetails)
      const finalUserId = userIdFromCaller || userIdFromToken || userIdFromStorage || userIdFromService;
      
      // Include user_id in the request body (backend expects 'user_id' not 'id')
      const updateDataWithId = { ...updateData };
      if (finalUserId) {
        updateDataWithId.user_id = finalUserId;
      }


      const response = await this.authenticatedFetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PUT',
        body: JSON.stringify(updateDataWithId)
      });

      let updatedProfile;
      try {
        const responseText = await response.text();

        if (!responseText || responseText.trim() === '') {
          // If response is empty, return the data we sent (some APIs return 200 with no body)
          updatedProfile = updateData;
        } else {
          updatedProfile = JSON.parse(responseText);
        }
      } catch (parseError) {
        // If parsing fails but we got a 200, the update might have succeeded
        // Return the data we sent as a fallback
        updatedProfile = updateData;
      }
      

      // Helper function to convert datetime to string if needed
      const formatDateTime = (value) => {
        if (!value) return '';
        // If it's already a string, return it
        if (typeof value === 'string') return value;
        // If it's a Date object or datetime, convert to ISO string
        if (value instanceof Date) return value.toISOString();
        // If it's an object with datetime properties, try to convert
        if (typeof value === 'object' && value.toString) {
          try {
            return new Date(value).toISOString();
          } catch (e) {
            return String(value);
          }
        }
        return String(value);
      };
      
      // Return transformed profile with safe property access
      return {
        id: updatedProfile.id || userId || '',
        firstName: updatedProfile.first_name || updatedProfile.firstName || updateData.first_name || '',
        lastName: updatedProfile.last_name || updatedProfile.lastName || updateData.last_name || '',
        email: updatedProfile.email || '',
        phone: updatedProfile.phone || updateData.phone || '',
        company: updatedProfile.company || updateData.company || '',
        organisation: updatedProfile.company || updateData.company || '',
        jobTitle: updatedProfile.job_title || updatedProfile.jobTitle || updateData.job_title || '',
        bio: updatedProfile.bio || updateData.bio || '',
        role: updatedProfile.role || updatedProfile.user_type || 'customer',
        profileImageUrl: updatedProfile.profile_image_url || updatedProfile.avatar_url || updateData.profile_image_url || '',
        createdAt: formatDateTime(updatedProfile.created_at || updatedProfile.createdAt),
        updatedAt: formatDateTime(updatedProfile.updated_at || updatedProfile.updatedAt)
      };
    } catch (error) {
      throw error;
    }
  }

  // Get user's bookings
  async getUserBookings() {
    try {

      if (!this.token) {
        throw new Error('No authentication token available');
      }

      // Get backend user ID from stored user data
      let userId = null;
      const storedUser = localStorage.getItem('user');

      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          userId = userData.id; // This is the backend user ID from JWT
        } catch (e) {
        }
      } else {
      }

      // Also try to get from service user
      if (!userId && this.user?.uid) {
        userId = this.user.uid;
      }


      if (!userId) {
        throw new Error('Backend user ID not available');
      }

      let userEmail = '';
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          userEmail = (userData.email || '').trim().toLowerCase();
        } catch (e) {
          // ignore
        }
      }
      if (!userEmail && this.user?.email) {
        userEmail = String(this.user.email).trim().toLowerCase();
      }

      // Profile list: backend matches customer_id, event_organizer_id, and guest contact email.
      let allBookings = [];
      const statuses = ['confirmed', 'completed', 'pending', 'cancelled'];
      
      // Helper function to parse API response (handles array or object responses)
      const parseBookingsResponse = (responseData) => {
        if (Array.isArray(responseData)) {
          return responseData;
        } else if (responseData && Array.isArray(responseData.bookings)) {
          return responseData.bookings;
        } else if (responseData && Array.isArray(responseData.data)) {
          return responseData.data;
        }
        return [];
      };
      
      // Helper to fetch bookings for a given query param
      const fetchBookingsByParam = async (paramName) => {
        const url = `${API_BASE_URL}/bookings?${paramName}=${userId}`;
        const response = await this.authenticatedFetch(url);
        if (!response.ok) {
          return [];
        }
        const responseData = await response.json();
        const bookingsArray = parseBookingsResponse(responseData);
        return bookingsArray;
      };

      // Single profile query (backend includes organiser + guest-email manual bookings).
      const paramNames = ['customer_id'];
      for (const paramName of paramNames) {
        try {
          const bookingsArray = await fetchBookingsByParam(paramName);
          if (bookingsArray.length > 0) {
            allBookings.push(...bookingsArray);

            // If we only got one status (e.g. 'confirmed'), try fetching other statuses
            const statusesFound = [...new Set(bookingsArray.map(b => b.status))];
            if (statusesFound.length === 1 && statusesFound[0] === 'confirmed') {
              for (const status of statuses) {
                if (status !== 'confirmed') {
                  try {
                    const statusUrl = `${API_BASE_URL}/bookings?${paramName}=${userId}&status=${status}`;
                    const statusResponse = await this.authenticatedFetch(statusUrl);
                    if (statusResponse.ok) {
                      const statusData = await statusResponse.json();
                      const statusBookings = parseBookingsResponse(statusData);
                      if (statusBookings.length > 0) {
                        allBookings.push(...statusBookings);
                      }
                    }
                  } catch (error) {
                    // Status filtering might not be supported, continue
                  }
                }
              }
            }
          }
        } catch (error) {
          // Continue with next param
        }
      }
      
      // Remove duplicates based on booking ID
      const seenIds = new Set();
      allBookings = allBookings.filter(booking => {
        if (booking.id && !seenIds.has(booking.id)) {
          seenIds.add(booking.id);
          return true;
        }
        return false;
      });
      

      // Use allBookings collected from API calls
      // Ensure bookings is an array
      const bookingsArray = Array.isArray(allBookings) ? allBookings : [];


      // Since we're now using the correct API parameters, we should get filtered results
      // But let's still do client-side filtering as a safety net
      // Convert userId to string for comparison to handle UUID string matching
      const userIdStr = String(userId);
      const emailLower = userEmail.toLowerCase();

      const guestEmailForBooking = (booking) => {
        const req = booking?.requirements || {};
        const ci = req.customer_info || req.customerInfo || {};
        return (ci.email || booking.contact_email || booking.contactEmail || '').trim().toLowerCase();
      };

      const userBookings = bookingsArray.filter((booking) => {
        const customerId = booking.customer_id ? String(booking.customer_id) : null;
        const eventOrganizerId = booking.event_organizer_id ? String(booking.event_organizer_id) : null;
        const guestEmail = guestEmailForBooking(booking);
        const isManual = String(booking.booking_source || '').toLowerCase() === 'manual';

        if (emailLower && guestEmail && guestEmail === emailLower) {
          return true;
        }
        if (eventOrganizerId === userIdStr) {
          return true;
        }
        if (customerId === userIdStr) {
          if (isManual && emailLower && guestEmail && guestEmail !== emailLower) {
            return false;
          }
          return true;
        }
        return false;
      });


      // if (userBookings.length === 0 && bookingsArray.length > 0) {
      //     customer_id: b.customer_id,
      //     event_organizer_id: b.event_organizer_id
      //   })));
      // }

      const getVenueImageUrl = (booking) => {
        const venue = booking?.venue || {};
        const selectedImage = (
          venue?.primary_image_url ||
          venue?.primaryImageUrl ||
          (Array.isArray(venue?.images)
            ? (venue.images.find((img) => img?.is_primary)?.image_url || venue.images[0]?.image_url)
            : null) ||
          venue?.image_url ||
          venue?.image ||
          booking?.venue_image_url ||
          booking?.venueImageUrl ||
          booking?.venue_image ||
          booking?.image_url ||
          booking?.image ||
          ''
        );

        return selectedImage;
      };

      // Transform booking data to match frontend format
      const transformedBookings = userBookings.map(booking => {
        const amounts = resolveBookingAmounts(booking, {
          venueId: booking.venue_id || booking.venueId || ''
        });
        const peopleCount = bookingHeadcount(booking) ?? 0;
        const transformed = {
          id: booking.id,
          venueName: booking.venue?.name || booking.venue_name || 'Unknown Venue',
          venueAddress: booking.venue?.address || booking.venue_address || '',
          venueImage: getVenueImageUrl(booking),
          date: booking.booking_date || booking.date || '',
          startTime: booking.start_time || booking.startTime || '',
          endTime: booking.end_time || booking.endTime || '',
          status: booking.status || 'pending',
          paymentStatus: booking.payment_status || booking.paymentStatus || 'pending',
          totalPrice: amounts.grandTotal,
          amountPending: amounts.amountPending,
          addOnsTotal: amounts.addOnsTotal,
          bookingDate: booking.created_at || booking.createdAt || '',
          venueId: booking.venue_id || booking.venueId || '',
          activity: resolveBookingActivity(booking),
          eventType: resolveBookingActivity(booking),
          attendees: peopleCount,
          headcount: peopleCount,
          duration: booking.duration || booking.duration_hours || 0,
          specialRequests: booking.special_requests || booking.notes || '',
          contactName: booking.contact_name || booking.customer_name || '',
          contactEmail: booking.contact_email || booking.customer_email || '',
          contactPhone: booking.contact_phone || booking.customer_phone || ''
        };

        return transformed;
      });

      return transformedBookings;
    } catch (error) {

      // Return empty array if API fails
      return [];
    }
  }

  async getBookingGuestContactMeta(bookingId) {
    let booking = null;
    if (this.token) {
      try {
        const response = await this.authenticatedFetch(`${API_BASE_URL}/bookings/${bookingId}`);
        if (response.ok) {
          booking = await response.json();
        }
      } catch {
        // Fall through to unauthenticated fetch for guest-email lookup.
      }
    }
    if (!booking) {
      const response = await fetch(`${API_BASE_URL}/bookings/${encodeURIComponent(String(bookingId))}`);
      if (!response.ok) {
        throw new Error(`Failed to load booking (${response.status})`);
      }
      booking = await response.json();
    }
    return bookingContactToThreadMeta(booking);
  }

  // Get specific booking details
  async getBookingDetails(bookingId) {
    try {
      if (!this.token) {
        throw new Error('No authentication token available');
      }

      const response = await this.authenticatedFetch(`${API_BASE_URL}/bookings/${bookingId}`);
      const booking = await response.json();
      const requirements = booking.requirements || {};
      const req =
        typeof requirements === 'string'
          ? (() => {
              try {
                return JSON.parse(requirements);
              } catch {
                return {};
              }
            })()
          : requirements;

      const amounts = resolveBookingAmounts(booking, {
        venueId: booking.venue_id || booking.venueId || ''
      });
      const peopleCount = bookingHeadcount(booking) ?? 0;

      return {
        id: booking.id,
        bookingReference: booking.booking_reference || booking.bookingReference || '',
        venueName: booking.venue?.name || booking.venue_name || 'Unknown Venue',
        venueAddress: booking.venue?.address || booking.venue_address || '',
        venueImage: booking.venue?.primary_image_url ||
          (Array.isArray(booking.venue?.images)
            ? (booking.venue.images.find((img) => img?.is_primary)?.image_url || booking.venue.images[0]?.image_url)
            : null) ||
          booking.venue?.image_url ||
          booking.venue?.image ||
          booking.venue_image_url ||
          booking.venue_image ||
          booking.image_url ||
          booking.image ||
          '',
        date: booking.booking_date || booking.date || '',
        startTime: booking.start_time || booking.startTime || '',
        endTime: booking.end_time || booking.endTime || '',
        status: booking.status || 'pending',
        totalPrice: amounts.grandTotal,
        amountPending: amounts.amountPending,
        addOnsTotal: amounts.addOnsTotal,
        basePrice: amounts.baseAmount,
        venueTotal: amounts.venueTotal,
        bookingDate: booking.created_at || booking.createdAt || '',
        venueId: booking.venue_id || booking.venueId || '',
        activity: resolveBookingActivity(booking),
        eventType: resolveBookingActivity(booking),
        attendees: peopleCount,
        headcount: peopleCount,
        specialRequests:
          booking.special_requests ||
          booking.notes ||
          req?.special_requirements ||
          '',
        contactName: booking.contact_name || booking.customer_name || req?.customer_info?.name || '',
        contactEmail:
          (req?.customer_info?.email || '').trim() ||
          booking.contact_email ||
          booking.customer_email ||
          '',
        contactPhone: booking.contact_phone || booking.customer_phone || req?.customer_info?.phone || '',
        customerId: booking.customer_id || booking.customerId || null,
        eventOrganizerId: booking.event_organizer_id || booking.eventOrganizerId || null,
        bookingSource: booking.booking_source || booking.source || req?.source || 'platform',
        // Additional booking details
        duration: booking.duration || booking.duration_hours || 0,
        hourlyRate: booking.hourly_rate || booking.hourlyRate || 0,
        cleaningFee: booking.cleaning_fee || booking.cleaningFee || 0,
        processingFee: amounts.processingFee,
        platformFee: amounts.platformFee,
        cancellationPolicy: booking.cancellation_policy || booking.cancellationPolicy || '',
        paymentStatus: booking.payment_status || booking.paymentStatus || 'pending',
        freeBooking: isFreeBooking(booking),
        requirements: req,
        final_amount: booking.final_amount,
        total_amount: booking.total_amount,
        payment_status: booking.payment_status || booking.paymentStatus,
      };
    } catch (error) {
      throw error;
    }
  }

  async getBookingNotes(bookingId, userId) {
    if (!this.token || !bookingId || !userId) {
      return [];
    }
    const params = new URLSearchParams({ user_id: String(userId) });
    const response = await this.authenticatedFetch(
      `${API_BASE_URL}/bookings/${bookingId}/notes?${params.toString()}`
    );
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    return Array.isArray(data) ? data : (data?.items ?? data?.notes ?? []);
  }

  // Cancel a booking
  async cancelBooking(bookingId, reason = '') {
    try {
      if (!this.token) {
        throw new Error('No authentication token available');
      }

      const response = await this.authenticatedFetch(`${API_BASE_URL}/bookings/${bookingId}/cancel`, {
        method: 'PUT',
        body: JSON.stringify({
          reason: reason,
          cancelled_at: new Date().toISOString()
        })
      });

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Update booking details
  async updateBooking(bookingId, updateData) {
    try {
      if (!this.token) {
        throw new Error('No authentication token available');
      }

      const response = await this.authenticatedFetch(`${API_BASE_URL}/bookings/${bookingId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Get booking statistics
  async getBookingStats() {
    try {
      if (!this.token) {
        throw new Error('No authentication token available');
      }

      const response = await this.authenticatedFetch(`${API_BASE_URL}/bookings/stats`);
      const stats = await response.json();

      return {
        totalBookings: stats.total_bookings || stats.totalBookings || 0,
        confirmedBookings: stats.confirmed_bookings || stats.confirmedBookings || 0,
        pendingBookings: stats.pending_bookings || stats.pendingBookings || 0,
        completedBookings: stats.completed_bookings || stats.completedBookings || 0,
        cancelledBookings: stats.cancelled_bookings || stats.cancelledBookings || 0,
        totalSpent: stats.total_spent || stats.totalSpent || 0,
        averageBookingValue: stats.average_booking_value || stats.averageBookingValue || 0,
        favoriteVenue: stats.favorite_venue || stats.favoriteVenue || null
      };
    } catch (error) {

      // Return default stats if API fails
      return {
        totalBookings: 0,
        confirmedBookings: 0,
        pendingBookings: 0,
        completedBookings: 0,
        cancelledBookings: 0,
        totalSpent: 0,
        averageBookingValue: 0,
        favoriteVenue: null
      };
    }
  }
}

// Create singleton instance
const userProfileService = new UserProfileService();

export default userProfileService;
